/**
 * "Trocar a empresa do negócio" (13.5, FR-83) — a ferramenta de limpeza da duplicata.
 *
 * O caso que a motivou é o Negócio 32 da Veluplast: nascido na ficha **sem** CNPJ, ao lado da ficha
 * certa. A gestão escolhe a Empresa certa no mesmo campo com sugestão do Novo negócio
 * (`CampoDeEmpresa`, 13.1) e o Negócio vai para ela com o mesmo código, histórico, Tarefas e
 * Orçamentos — também ganho ou perdido.
 *
 * - **"Criar nova empresa"** abre a ficha do "+ Empresa" num modal (EXPERIENCE.md:124), com o
 *   Responsável do Negócio como Responsável inicial; a criada fica escolhida.
 * - **A linha "De → Para"** diz o que vai acontecer, e "Trocar" fica desabilitado sem escolha ou com
 *   a mesma Empresa.
 * - **A nota da ida ao Carga Máquina:** com Orçamento vinculado, a Empresa escolhida é cadastrada
 *   no Carga Máquina se tem CNPJ ou CPF, e sem documento ganha o aviso de CNPJ — um efeito que não se
 *   desfaz trocando de volta (texto da 13.11c; o nome `NOTA_DA_VIRADA` ficou da 13.5).
 * - **O `PUT` leva a Empresa que a ficha mostrava** (`empresa_anterior_id`): se outro gestor já a
 *   trocou, a resposta é `409`, verbatim, e o diálogo fica aberto.
 *
 * A ficha empilhada é **irmã** do diálogo, e não filha: o `Esc` e o clique no fundo dela não podem
 * fechar a troca de baixo (o `Dialogo` cede o `Esc` a quem está por cima).
 */

import { useId, useState } from 'react'

import { useEmpresa } from '@/modulos/cadastro/api'
import { CampoDeEmpresa, type EmpresaEscolhida } from '@/modulos/cadastro/CampoDeEmpresa'
import { nomeExibidoDaEmpresa } from '@/modulos/cadastro/nome-da-empresa'
import { ModalDaNovaEmpresa } from '@/modulos/cadastro/ModalDaNovaEmpresa'
import { useTrocarEmpresa } from './api'
import { Dialogo } from './Dialogo'

export const TITULO_DA_TROCA = 'Trocar a empresa do negócio'
export const ROTULO_DO_TROCAR = 'Trocar'
export const NOTA_DA_VIRADA =
  'Se o negócio tem orçamento, a empresa escolhida é cadastrada no Carga Máquina quando tem ' +
  'CNPJ ou CPF; sem documento, a ficha dela passa a pedir o CNPJ.'
/** O nome enquanto a ficha achada pelo ERP ainda está sendo lida. */
const EMPRESA_JA_CADASTRADA = 'a empresa já cadastrada'

/** O anúncio do sucesso — com o nome da Empresa nova (13.7: o nome exibido). */
export function textoDaTroca(nomeDaEmpresa: string): string {
  return `Empresa do negócio trocada para ${nomeDaEmpresa}.`
}

export interface PropriedadesDaTroca {
  negocioId: string
  codigo: number
  empresaAtual: EmpresaEscolhida
  /** O Responsável do Negócio — o inicial da Empresa criada aqui. */
  responsavelDoNegocio: string
  aoFechar: () => void
  /** A troca deu certo: o nome da Empresa nova, para o anúncio. */
  aoTrocada: (nomeDaEmpresa: string) => void
}

export function TrocaDaEmpresaDoNegocio({
  negocioId,
  codigo,
  empresaAtual,
  responsavelDoNegocio,
  aoFechar,
  aoTrocada,
}: PropriedadesDaTroca) {
  const idDoCampo = useId()
  const trocar = useTrocarEmpresa(negocioId)
  const [escolhida, setEscolhida] = useState<EmpresaEscolhida | null>(null)
  const [fichaAberta, setFichaAberta] = useState<string | null>(null)
  const [chaveDoCampo, setChaveDoCampo] = useState(0)

  /* A ficha que o ERP achou (`aoEncontrar`) chega so com o `id`: o nome vem da leitura dela. */
  const semNome = escolhida !== null && escolhida.nome === ''
  const lida = useEmpresa(semNome ? escolhida.id : undefined)
  const nomeEscolhido =
    escolhida === null
      ? ''
      : escolhida.nome ||
        (lida.data === undefined ? EMPRESA_JA_CADASTRADA : nomeExibidoDaEmpresa(lida.data))
  const mesma = escolhida !== null && escolhida.id === empresaAtual.id
  /* Enquanto o nome da ficha achada pelo ERP não chegou, "Trocar" espera: a linha "De → Para" e o
     anúncio precisam do nome dela, e não de "a empresa já cadastrada". */
  const lendoONome = semNome && lida.data === undefined
  const confirmar = () => {
    if (escolhida === null || mesma || lendoONome) {
      return
    }
    trocar.mutate(
      { empresaId: escolhida.id, empresaAnteriorId: empresaAtual.id },
      {
        onSuccess: () => {
          aoTrocada(nomeEscolhido)
        },
      },
    )
  }

  return (
    <>
      <Dialogo
        titulo={TITULO_DA_TROCA}
        subtitulo={`Negócio ${String(codigo)}`}
        rotuloDoConfirmar={ROTULO_DO_TROCAR}
        confirmarDesabilitado={escolhida === null || mesma || lendoONome}
        ocupado={trocar.isPending}
        erro={trocar.isError ? trocar.error : semNome && lida.isError ? lida.error : undefined}
        aoFechar={aoFechar}
        aoConfirmar={confirmar}
      >
        <CampoDeEmpresa
          key={chaveDoCampo}
          id={idDoCampo}
          escolhida={escolhida === null ? null : { id: escolhida.id, nome: nomeEscolhido }}
          aoEscolher={(empresa) => {
            trocar.reset()
            setEscolhida(empresa)
          }}
          aoCriarNova={(texto) => {
            setFichaAberta(texto)
          }}
          //: 13.20b2 (code review, B7/D): a lista por cima do corpo curto do diálogo, nunca cortada.
          flutuante
        />
        {escolhida === null ? null : (
          <p className="ficha__dialogo-aviso">
            {`De: ${empresaAtual.nome} → Para: ${nomeEscolhido}`}
          </p>
        )}
        <p className="ficha__dialogo-aviso">{NOTA_DA_VIRADA}</p>
      </Dialogo>
      {fichaAberta === null ? null : (
        <ModalDaNovaEmpresa
          textoInicial={fichaAberta}
          telefone={undefined}
          responsavelInicial={responsavelDoNegocio}
          aoCancelar={() => {
            setFichaAberta(null)
          }}
          aoEscolher={(empresa) => {
            trocar.reset()
            setEscolhida(empresa)
            setChaveDoCampo((chave) => chave + 1)
            setFichaAberta(null)
          }}
          aoEncontrar={(empresaId) => {
            //: O CNPJ que o ERP já conhece: a ficha existente fica escolhida, e o nome vem da
            //: leitura dela (`useEmpresa`).
            trocar.reset()
            setEscolhida({ id: empresaId, nome: '' })
            setChaveDoCampo((chave) => chave + 1)
            setFichaAberta(null)
          }}
        />
      )}
    </>
  )
}
